/**
 * WhisperAdapter — wraps @huggingface/transformers + Whisper base for
 * on-device speech recognition (Arch §2.5, §3.1).
 *
 * V1 §A2: model is fetched on first use (typically kicked off in the
 * background from the Consent screen acknowledgment) and cached via the
 * Cache Storage API for offline reuse. Execution prefers WebGPU when the
 * browser supports it; falls back to WASM otherwise.
 *
 * Audio input: the adapter takes the Blob produced by `RecordingPipeline`
 * (webm/opus from MediaRecorder), decodes it via Web Audio API,
 * downmixes to mono, and resamples to 16 kHz — the input format the
 * Whisper pipeline expects.
 *
 * Failure mode: if the model fails to load or transcribe throws, the
 * adapter returns an empty transcript with a low confidence sentinel.
 * This is the graceful degradation called out in PRD §10.2 (the grade
 * pipeline still runs against whatever transcript it received, even if
 * empty / partial).
 */

// Type-only import — erased at compile time so the actual @huggingface/transformers
// module is NOT included in the initial bundle. The runtime import below
// loads it on demand via dynamic import(), which lets Vite split it into
// its own chunk. The Whisper library is several MB of code (plus the
// onnxruntime-web WASM it pulls in), so keeping it out of the main chunk
// is a meaningful initial-load win — particularly because the user only
// needs it after Consent → SessionConfig, by which point we're already
// eager-loading the model in the background.
import type { Pipeline } from '@huggingface/transformers';

export type WhisperLoadStatus =
  | { phase: 'idle' }
  | { phase: 'loading'; progress: number }
  | { phase: 'ready' }
  | { phase: 'error'; message: string };

export interface TranscriptionResult {
  text: string;
  durationMs: number;
  confidence: number | null;
}

export interface WhisperAdapter {
  /**
   * Begin (or rejoin) loading the Whisper model. Safe to call multiple
   * times — the second-and-later callers share the in-flight load promise.
   * Resolves once the model is ready to transcribe.
   */
  ensureLoaded(): Promise<void>;

  /** True once the model is loaded and ready for transcription. */
  isReady(): boolean;

  /** Current load state (idle / loading + progress / ready / error). */
  getStatus(): WhisperLoadStatus;

  /**
   * Subscribe to status changes. Returns an unsubscribe function. Useful
   * for progress UIs (Consent → SessionConfig hand-off).
   */
  subscribe(listener: (status: WhisperLoadStatus) => void): () => void;

  /**
   * Transcribe an audio blob. Awaits model load if necessary. On error
   * resolves with empty text rather than throwing — per PRD §10.2 the
   * grade pipeline runs on whatever transcript is available.
   */
  transcribe(audio: Blob): Promise<TranscriptionResult>;
}

// English-only variant. Same parameter count + download size as
// whisper-base, but capacity isn't split across 99 languages — typically
// a 15-25% relative WER reduction on English audio. Since RecordingPipeline
// targets English narration, the multilingual variant was paying capacity
// for languages we never use.
const MODEL_ID = 'onnx-community/whisper-base.en';
const TARGET_SAMPLE_RATE = 16_000;

/**
 * Domain-bias prompt fed to Whisper's decoder via the `prompt` argument.
 * The model uses this to bias toward correctly-spelled vocabulary in the
 * upcoming audio — a known underused win for jargon-heavy domains.
 *
 * Style: metadata-and-vocabulary clusters rather than a fictional poker
 * scenario. Whisper has a documented failure mode where prompt text that
 * reads like a story can leak into the transcript ("continuing" the
 * narration). Listing vocabulary as terminology avoids that.
 *
 * Vocabulary sourced from `/reference/` per the project source-of-truth
 * rule: hand classes from `/reference/02_glossary_hand_types.yaml`,
 * board textures from `/reference/03_glossary_board_types.yaml`,
 * archetypes from `/reference/01_glossary_player_types.yaml`,
 * polarized/linear from `/reference/11_heuristics_database.yaml`
 * `sizing_concepts`. Position seat names limited to heads-up (SB/BB/BTN)
 * per V1 scope.
 *
 * If you change `/reference/` glossaries (rename a hand class, add a
 * sizing concept, etc.), update this prompt to stay in lockstep.
 */
const INITIAL_PROMPT =
  'Postflop poker decision narration. Common terms: preflop, flop, turn, river, ' +
  'in position (IP), out of position (OOP), small blind (SB), big blind (BB), ' +
  'button (BTN), single raised pot (SRP), 3-bet pot (3BP), stack-to-pot ratio (SPR), ' +
  'pot odds, equity, range, default, target, c-bet, donk bet, range bet, ' +
  'polarized, linear, capped, uncapped, blocker, board texture, runout, ' +
  'Dynamic Wet, Dynamic Dry, Static Wet, Static Dry, bluff catch checklist, ' +
  'showdown. Hand classes: Can Play For Stacks (CPFS), Thick Value, Thin Value, ' +
  'Combo Draws, High Equity Draws, Low Equity Draws, Showdown Value (SDV), Air. ' +
  'Opponent archetypes: Fish, Whale, Trying Rec, Reg.';

class V1WhisperAdapter implements WhisperAdapter {
  private transcriber: Pipeline | null = null;
  private loadPromise: Promise<void> | null = null;
  private status: WhisperLoadStatus = { phase: 'idle' };
  private listeners: Array<(status: WhisperLoadStatus) => void> = [];

  isReady(): boolean {
    return this.transcriber !== null;
  }

  getStatus(): WhisperLoadStatus {
    return this.status;
  }

  subscribe(listener: (status: WhisperLoadStatus) => void): () => void {
    this.listeners.push(listener);
    // Immediately invoke with current status so callers don't need to
    // separately read getStatus().
    listener(this.status);
    return () => {
      this.listeners = this.listeners.filter((l) => l !== listener);
    };
  }

  ensureLoaded(): Promise<void> {
    if (this.transcriber) return Promise.resolve();
    if (this.loadPromise) return this.loadPromise;

    this.setStatus({ phase: 'loading', progress: 0 });

    this.loadPromise = (async () => {
      try {
        // Dynamic import — see the comment on the type-only import above.
        // This is what causes Vite to emit a separate chunk for the
        // Whisper library instead of bundling it into main.
        const { pipeline } = await import('@huggingface/transformers');
        const device = await pickDevice();
        // Per-file progress from Transformers.js. We aggregate to a single
        // 0..1 average across files for the UI.
        const fileProgress: Record<string, number> = {};
        const computeOverall = () => {
          const values = Object.values(fileProgress);
          if (values.length === 0) return 0;
          const sum = values.reduce((a, b) => a + b, 0);
          return Math.min(1, sum / values.length);
        };

        const transcriber = await pipeline(
          'automatic-speech-recognition',
          MODEL_ID,
          {
            device,
            progress_callback: (data: unknown) => {
              // Transformers.js callback shape: {status, file, progress, ...}.
              // Only the {status: 'progress', file, progress: 0..100} variant
              // contributes to our aggregate.
              if (
                typeof data === 'object' &&
                data !== null &&
                'status' in data &&
                'file' in data
              ) {
                const d = data as {
                  status: string;
                  file: string;
                  progress?: number;
                };
                if (d.status === 'progress' && typeof d.progress === 'number') {
                  fileProgress[d.file] = Math.min(1, d.progress / 100);
                } else if (d.status === 'done') {
                  fileProgress[d.file] = 1;
                }
                this.setStatus({
                  phase: 'loading',
                  progress: computeOverall(),
                });
              }
            },
          },
        );
        this.transcriber = transcriber as Pipeline;
        this.setStatus({ phase: 'ready' });
      } catch (err) {
        const message = err instanceof Error ? err.message : String(err);
        this.setStatus({ phase: 'error', message });
        this.loadPromise = null;
        throw err;
      }
    })();

    return this.loadPromise;
  }

  async transcribe(audio: Blob): Promise<TranscriptionResult> {
    try {
      await this.ensureLoaded();
    } catch (err) {
      console.error('[WhisperAdapter] ensureLoaded failed:', err);
      return { text: '', durationMs: 0, confidence: null };
    }
    if (!this.transcriber) {
      console.warn(
        '[WhisperAdapter] transcribe called with no transcriber loaded',
      );
      return { text: '', durationMs: 0, confidence: null };
    }
    if (audio.size === 0) {
      console.warn(
        '[WhisperAdapter] transcribe called with empty audio blob — likely a recording-pipeline race or mic permission revoked mid-session',
      );
      return { text: '', durationMs: 0, confidence: null };
    }

    let float32: Float32Array;
    try {
      float32 = await blobToMono16k(audio);
    } catch (err) {
      console.error(
        '[WhisperAdapter] audio decode/resample failed:',
        err,
        { blobSize: audio.size, type: audio.type },
      );
      return { text: '', durationMs: 0, confidence: null };
    }

    try {
      const start = performance.now();
      // Pipeline accepts a Float32Array at 16 kHz. The options:
      //   language: 'en' — skip auto-detection, which occasionally
      //     misclassifies short English utterances and then transcribes
      //     them as if they were another language. (Redundant on .en
      //     models, kept for defense in depth.)
      //   task: 'transcribe' — pin to transcription rather than the
      //     translate-into-English mode.
      //   chunk_length_s: 30 — Whisper's native context window is ~30s.
      //     Without chunking specified, audio longer than that gets
      //     silently truncated (this is what was cutting off long
      //     decision narrations after a few sentences).
      //   stride_length_s: 5 — overlap between chunks, prevents
      //     words on the chunk boundary from being dropped.
      //   prompt: INITIAL_PROMPT — domain vocabulary bias. See the
      //     INITIAL_PROMPT comment above for what it contains and why.
      const result = (await this.transcriber(float32, {
        language: 'en',
        task: 'transcribe',
        chunk_length_s: 30,
        stride_length_s: 5,
        prompt: INITIAL_PROMPT,
      })) as
        | { text?: string }
        | Array<{ text?: string }>;
      const elapsed = performance.now() - start;
      // Pipeline can return a single object or array depending on options.
      const text = Array.isArray(result)
        ? result.map((r) => r.text ?? '').join(' ').trim()
        : (result.text ?? '').trim();
      if (!text) {
        console.warn(
          '[WhisperAdapter] inference produced empty text',
          { samples: float32.length, elapsedMs: Math.round(elapsed) },
        );
      }
      return { text, durationMs: elapsed, confidence: null };
    } catch (err) {
      console.error('[WhisperAdapter] inference failed:', err);
      return { text: '', durationMs: 0, confidence: null };
    }
  }

  private setStatus(status: WhisperLoadStatus): void {
    this.status = status;
    this.listeners.forEach((l) => l(status));
  }
}

// ============================================================================
// Helpers
// ============================================================================

/**
 * Decide which execution device Transformers.js should use. WebGPU is
 * preferred for speed; falls back to WASM if the browser doesn't expose
 * `navigator.gpu` or if the adapter request fails.
 */
async function pickDevice(): Promise<'webgpu' | 'wasm'> {
  // Vendor-prefixed check kept loose to handle Safari/older Chromes.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const gpu = (navigator as any).gpu;
  if (!gpu || typeof gpu.requestAdapter !== 'function') return 'wasm';
  try {
    const adapter = await gpu.requestAdapter();
    return adapter ? 'webgpu' : 'wasm';
  } catch {
    return 'wasm';
  }
}

/**
 * Decode a MediaRecorder Blob (webm/opus) into a Float32Array of mono
 * audio resampled to 16 kHz. Returns an empty array on any decode error.
 */
async function blobToMono16k(blob: Blob): Promise<Float32Array> {
  const arrayBuffer = await blob.arrayBuffer();

  // Decode at the native sample rate first. AudioContext doesn't reliably
  // honor a custom sampleRate constructor arg in every browser, so we
  // resample explicitly with OfflineAudioContext below.
  const tempCtx = new AudioContext();
  let decoded: AudioBuffer;
  try {
    decoded = await tempCtx.decodeAudioData(arrayBuffer.slice(0));
  } finally {
    await tempCtx.close();
  }

  // Resample + downmix to mono via OfflineAudioContext.
  const offlineCtx = new OfflineAudioContext(
    1,
    Math.ceil(decoded.duration * TARGET_SAMPLE_RATE),
    TARGET_SAMPLE_RATE,
  );
  const source = offlineCtx.createBufferSource();
  source.buffer = decoded;
  source.connect(offlineCtx.destination);
  source.start(0);
  const rendered = await offlineCtx.startRendering();
  return rendered.getChannelData(0);
}

// ============================================================================
// Singleton
// ============================================================================

export const whisperAdapter: WhisperAdapter = new V1WhisperAdapter();
